import { useEffect, useState } from 'react';
import { filterSupportedEventListenerOptions } from '../../utils/index.js';
import useDesignTokens from '../useDesignTokens/index.js';

/**
 * A hook to get the current breakpoint.
 *
 * Generally, we will want to do as much of our styling as possible with CSS.
 * But, there are a small number of cases where we want to swap a property
 * or entire component based on the current breakpoint.
 *
 * The hook uses `window.matchMedia` with listeners to check when the breakpoint
 * has changed (supported by IE >= 10, see https://caniuse.com/matchmedia). This
 * is significantly less expensive than binding a resize event listener.
 */
const useBreakpoints = () => {
  const breakpoints = ['xs', 'sm', 'md', 'lg'];
  const {
    grid
  } = useDesignTokens();
  const [currentBreakpoint, setCurrentBreakpoint] = useState();
  useEffect(() => {
    const mediaQueries = breakpoints.slice(1).map(bp => ({
      bp,
      query: window.matchMedia(`(min-width: ${grid[bp]}px)`)
    }));
    const evtListenerOpts = filterSupportedEventListenerOptions({
      passive: true
    });

    /**
     * Check all media queries and set the breakpoint to the highest that matches.
     */
    const setHighestBreakpoint = () => {
      const highestBreakpoint = mediaQueries.reduce((currentBp, {
        query,
        bp: nextBp
      }) => query.matches ? nextBp : currentBp, breakpoints[0]);
      setCurrentBreakpoint(highestBreakpoint);
    };
    mediaQueries.forEach(({
      query
    }) => {
      // Use `addListener` as a fallback for older browsers
      if (!query.addEventListener && query.addListener) {
        query.addListener(setHighestBreakpoint);
        return;
      }

      // If running IE 9, no media queries for you
      if (!query.addEventListener) {
        return;
      }
      query.addEventListener('change', setHighestBreakpoint, evtListenerOpts);
    });
    setHighestBreakpoint();
    return () => {
      mediaQueries.forEach(({
        query
      }) => {
        query.removeEventListener('change', setHighestBreakpoint);
      });
    };
  }, []);

  /**
   * Check if the screen is greater than or equal to the given breakpoint.
   */
  const isAboveBreakpoint = (bp, fallbackState = false) => {
    if (!currentBreakpoint) {
      return fallbackState; // for when the useEffect hasn't fired yet
    }
    return breakpoints.indexOf(bp) <= breakpoints.indexOf(currentBreakpoint);
  };

  /**
   * Check if the screen is less than or equal to the given breakpoint.
   */
  const isBelowBreakpoint = (bp, fallbackState = false) => {
    if (!currentBreakpoint) {
      return fallbackState; // for when the useEffect hasn't fired yet
    }
    return breakpoints.indexOf(bp) >= breakpoints.indexOf(currentBreakpoint);
  };
  return {
    currentBreakpoint,
    isAboveBreakpoint,
    isBelowBreakpoint
  };
};
export default useBreakpoints;